import {
  ShieldCheck,
  Lock,
  KeyRound,
  ServerCog,
  FileText,
  ExternalLink,
  Mail,
  Clock,
  AlertTriangle
} from 'lucide-react';

/**
 * Trust Center — in-dashboard mirror of the public /security page on
 * nurev.io. Today this is a static posture summary plus mailto CTAs;
 * the "per-customer audit log + key activity timeline" callouts at the
 * bottom flag what's coming next so customers can plan against it.
 *
 * Wording for the posture bullets MUST stay in sync with what we
 * publish on nurev.io/security so an inference customer reading both
 * surfaces never sees contradictions. If you change one, change the
 * other.
 */

type Posture = {
  icon: typeof ShieldCheck;
  title: string;
  body: string;
};

const POSTURE: Posture[] = [
  {
    icon: ShieldCheck,
    title: 'Inference payloads are not retained',
    body:
      'Request bodies for /v1/embeddings and /v1/chat/completions are processed in memory by the routing tier, forwarded to the supply provider, and discarded once the response is returned. We do not write prompt text, embedding inputs, completion outputs, or message content to durable storage anywhere in the request path.'
  },
  {
    icon: KeyRound,
    title: 'API keys are bcrypt-hashed server-side',
    body:
      'Raw keys are never stored. The console persists a bcrypt hash and the first 13 characters (the nrev_… prefix you see in the key list). The raw value is shown exactly once at mint time — copy it to your secrets manager and it cannot be displayed again.'
  },
  {
    icon: Lock,
    title: 'TLS 1.3 end-to-end',
    body:
      'Every endpoint — the API at api.app.nurev.io, this console, and the marketing site — terminates TLS 1.3 with modern cipher suites. Older protocol versions and weak ciphers are disabled at the load balancer. Certificates rotate automatically with a 30-day buffer.'
  },
  {
    icon: ServerCog,
    title: 'Webhooks are HMAC-SHA-256 signed',
    body:
      'Every outbound webhook carries an X-NUREV-Signature header containing an HMAC of the raw body, computed with a 32-byte per-customer secret you can rotate from the Webhook screen. Use the self-serve test delivery to validate your verification code before depending on it.'
  },
  {
    icon: FileText,
    title: 'No fund custody — regulated payment partners only',
    body:
      'NUREV never holds customer funds. USD payouts to operators flow through licensed money-transmitter partners. Card data is handled by a PCI-DSS Level 1 payment-rails provider — NUREV systems see only tokenized references, keeping our environment out of PCI scope.'
  },
  {
    icon: Clock,
    title: 'Job records keep metadata only — 90-day TTL',
    body:
      'Per-request metadata (model, endpoint, token counts, latency, cost, provider tier) is retained 90 days so you can audit usage on the Usage screen and we can debug routing issues. None of those records contain your input or output content.'
  }
];

const COMING_SOON: { title: string; body: string }[] = [
  {
    title: 'Per-customer audit log',
    body:
      'Timeline of sign-ins, key mints, key revokes, webhook secret rotations, and configuration changes — exportable for your own compliance trail.'
  },
  {
    title: 'Per-key activity stream',
    body:
      'Live + historical request counts, last-used timestamps, and IP origins per key — for spotting credential leakage early.'
  },
  {
    title: 'Per-key IP allowlist + model restriction',
    body:
      'Lock a key to a specific egress IP range, or to a subset of the model catalog, so a leaked key has bounded blast radius.'
  }
];

export function TrustScreen() {
  return (
    <div className="space-y-10">
      <header>
        <h1 className="text-2xl font-bold text-nurev-text">Trust Center</h1>
        <p className="mt-2 text-sm text-nurev-muted">
          How NUREV treats your data — the same posture we publish at{' '}
          <a
            href="https://nurev.io/security/"
            target="_blank"
            rel="noreferrer"
            className="text-nurev-purple-light hover:underline"
          >
            nurev.io/security
          </a>
          , here so you have it without leaving the console.
        </p>
      </header>

      {/* ─── Posture grid ─── */}
      <section className="space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-widest text-nurev-purple-light">
          Current posture
        </h2>
        <div className="space-y-3">
          {POSTURE.map(({ icon: Icon, title, body }) => (
            <div key={title} className="card">
              <div className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-nurev-purple/15 text-nurev-purple-light">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-nurev-text">
                    {title}
                  </h3>
                  <p className="mt-1 text-sm leading-relaxed text-nurev-text-soft">
                    {body}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Compliance posture (honest) ─── */}
      <section className="space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-widest text-nurev-purple-light">
          Compliance posture
        </h2>
        <div className="card border-amber-500/30 bg-amber-500/5">
          <div className="flex gap-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/15 text-amber-300">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-nurev-text">
                SOC 2 Type I — on the roadmap, prioritized by customer demand.
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-nurev-text-soft">
                We have not yet pursued SOC 2 certification. The controls a
                SOC 2 audit verifies — access management, change management,
                incident response, vendor management, secure SDLC — are
                largely in place today; we just haven't paid for an external
                auditor to attest to them yet. If your procurement gate
                requires SOC 2, tell us and we'll start the engagement with
                a target of Type I within 90 days. In the meantime, we'll
                walk your security team through our current control set on
                request.
              </p>
              <p className="mt-2 text-sm leading-relaxed text-nurev-text-soft">
                A signable <strong className="text-nurev-text">DPA</strong>{' '}
                (Data Processing Agreement) and our{' '}
                <strong className="text-nurev-text">sub-processor list</strong>{' '}
                (under short mutual NDA) are both available on request.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Coming soon ─── */}
      <section className="space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-widest text-nurev-purple-light">
          On the roadmap
        </h2>
        <p className="text-sm text-nurev-muted">
          The per-customer security telemetry below isn't shipped in the
          console yet — when it lands, it'll show up here without you having
          to do anything. If any of these would unblock a procurement gate
          you're hitting today, mention it in your security questionnaire
          request and we'll prioritize accordingly.
        </p>
        <div className="grid gap-3 md:grid-cols-3">
          {COMING_SOON.map(({ title, body }) => (
            <div
              key={title}
              className="rounded-xl border border-dashed border-nurev-border bg-nurev-surface/50 p-4"
            >
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-nurev-purple/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-nurev-purple-light">
                <Clock className="h-3 w-3" /> Coming soon
              </div>
              <h3 className="text-sm font-semibold text-nurev-text">
                {title}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-nurev-text-soft">
                {body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Contact ─── */}
      <section className="card">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-base font-semibold text-nurev-text">
              Need the formal version for your security team?
            </h2>
            <p className="mt-1 text-sm text-nurev-text-soft">
              Security questionnaire, DPA, sub-processor list, and our
              current control narrative are all available on request — usually
              within one business day.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              className="btn-primary"
              href="mailto:security@nurev.io?subject=Security%20questionnaire%20request"
            >
              <Mail className="h-4 w-4" />
              Request questionnaire
            </a>
            <a
              className="btn-secondary"
              href="https://nurev.io/security/"
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="h-4 w-4" />
              Public posture page
            </a>
          </div>
        </div>
      </section>

      <p className="text-center text-xs text-nurev-muted">
        Vulnerability or security issue? Email{' '}
        <a
          href="mailto:security@nurev.io"
          className="text-nurev-purple-light hover:underline"
        >
          security@nurev.io
        </a>
        . Safe-harbour policy for good-faith research applies — see the
        public posture page for the full disclosure terms.
      </p>
    </div>
  );
}
